/**
 * Customer Detail Page
 * Shows customer info, optional account summary, and the customer's
 * properties (each links to its PropertyDetailPage). ADMIN/OFFICE users can delete properties inline (add/edit happen
 * on PropertyFormPage) and attach discounts to the customer or to individual
 * properties. The Lead Source section shows the current source and the
 * append-only history of changes (set on CustomerFormPage).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createAPIClient, ICustomersApi, IDispatchApi, IPropertiesApi, IDiscountsApi, ILeadSourcesApi, IRecurringJobsApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { canReadJobs } from '../utils/permissions';
import { canManageCustomerPortal, canManageCustomers, canManagePortalRollout, canManageProperties, canManageRecurring, canSendNotifications, canUseOnlinePayments, canViewRecurring, canViewReports } from '../utils/permissions';
import { CustomerPaymentMethodsCard } from '../features/online-payments/CustomerPaymentMethodsCard';
import { CustomerCreditCard } from '../features/customer-credit/CustomerCreditWidgets';
import { canManageBundles, canManageJobs } from '../utils/permissions';
import { BundlesSection } from '../features/service-bundles/BundlesSection';
import { CustomerCommunicationPanel } from '../components/CustomerCommunicationPanel';
import { CustomerPortalPanel } from '../components/CustomerPortalPanel';
import { CustomerRolloutPanel } from '../components/CustomerRolloutPanel';
import { formatRatePercent } from '../utils/taxRate';
import { formatSize } from '../utils/priceChart';
import { CustomerStatusBadge } from '../components/CustomerStatusBadge';
import { CustomerJobsSection } from '../components/CustomerJobsSection';
import { RecurringServicesSection } from '../components/RecurringServicesSection';
import { AttachmentsSection } from '../components/AttachmentsSection';
import { ConversationsSection } from '../components/ConversationsSection';
import { DiscountAttachmentPanel } from '../components/DiscountAttachmentPanel';
import { Badge, StatCard, formatMoney } from '../components/DetailWidgets';
import { propertyDetailPath } from './propertyUi';
import {
  primaryButtonClass,
  rowActionClass,
  rowDangerActionClass,
  secondaryButtonClass,
} from '../components/formStyles';
import { Toast } from '../components/Toast';
import { DefaultRoutePanel } from '../components/RouteControls';

const BILLING_METHOD_LABELS: Record<types.BillingMethod, string> = {
  PER_VISIT: 'Per Visit',
  CONSOLIDATED: 'Consolidated',
};

export function CustomerDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const apiClient = React.useMemo<ICustomersApi & IPropertiesApi & IDiscountsApi & ILeadSourcesApi & IRecurringJobsApi & IDispatchApi>(() => createAPIClient(), []);
  const canManage = canManageCustomers(user);
  const canEditProperties = canManageProperties(user);
  const canStatement = canViewReports(user);
  const canSeeRecurring = canViewRecurring(user);
  const canEditRecurring = canManageRecurring(user);
  const canSeeJobs = canReadJobs(user);

  // State
  const [customer, setCustomer] = useState<types.Customer | null>(null);
  const [properties, setProperties] = useState<types.Property[]>([]);
  const [propertiesError, setPropertiesError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Property delete state
  const [confirmDeletePropertyId, setConfirmDeletePropertyId] = useState<string | null>(null);
  const [deletingPropertyId, setDeletingPropertyId] = useState<string | null>(null);

  // Discount attachments. A null list means it has not loaded yet.
  const [discountCatalog, setDiscountCatalog] = useState<types.Discount[]>([]);
  const [discountCatalogError, setDiscountCatalogError] = useState<string | null>(null);
  const [customerDiscounts, setCustomerDiscounts] = useState<types.Discount[] | null>(null);
  const [customerDiscountsError, setCustomerDiscountsError] = useState<string | null>(null);
  const [propertyDiscounts, setPropertyDiscounts] = useState<Record<string, types.Discount[]>>({});
  const [propertyDiscountErrors, setPropertyDiscountErrors] = useState<Record<string, string>>({});
  const [expandedDiscountPropertyId, setExpandedDiscountPropertyId] = useState<string | null>(null);

  // Lead source. A null history means it has not loaded yet.
  const [leadSources, setLeadSources] = useState<types.LeadSource[]>([]);
  const [leadSourceHistory, setLeadSourceHistory] = useState<types.CustomerLeadSourceHistoryEntry[] | null>(null);
  const [leadSourceError, setLeadSourceError] = useState<string | null>(null);

  // Recurring services at this customer's properties. A null list means it
  // has not loaded yet; a failure only affects this section.
  const [recurring, setRecurring] = useState<types.RecurringSchedule[] | null>(null);
  const [recurringError, setRecurringError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !canSeeRecurring) return;
    let active = true;
    setRecurring(null);
    setRecurringError(null);
    apiClient.listRecurringSchedules(1, 100, { customerId: id })
      .then((result) => { if (active) setRecurring(result.data); })
      .catch((err) => { if (active) setRecurringError(getErrorMessage(err, 'Failed to load recurring services')); });
    return () => { active = false; };
  }, [apiClient, id, canSeeRecurring]);

  // Load data
  useEffect(() => {
    if (!id) {
      setError('No customer specified');
      setIsLoading(false);
      return;
    }

    const loadCustomer = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setPropertiesError(null);

        // Properties are loaded independently so a failure there does not
        // hide the customer record. With customerId set, the backend returns
        // all of the customer's properties in one response (no paging).
        const [customerResult, propertiesResult] = await Promise.allSettled([
          apiClient.getCustomer(id),
          apiClient.listProperties(id),
        ]);

        if (customerResult.status === 'rejected') {
          throw customerResult.reason;
        }
        setCustomer(customerResult.value);

        if (propertiesResult.status === 'fulfilled') {
          setProperties(propertiesResult.value.data);
        } else {
          setProperties([]);
          setPropertiesError(getErrorMessage(propertiesResult.reason, 'Failed to load properties'));
        }
      } catch (err) {
        setCustomer(null);
        setError(getErrorMessage(err, 'Failed to load customer'));
      } finally {
        setIsLoading(false);
      }
    };

    loadCustomer();
  }, [apiClient, id]);

  useEffect(() => {
    if (!id) return;

    const loadCustomerDiscounts = async () => {
      try {
        setCustomerDiscounts(null);
        setCustomerDiscountsError(null);
        setCustomerDiscounts(await apiClient.listCustomerDiscounts(id));
      } catch (err) {
        setCustomerDiscountsError(getErrorMessage(err, 'Failed to load discounts'));
      }
    };

    loadCustomerDiscounts();
  }, [apiClient, id]);

  useEffect(() => {
    if (!id) return;

    const loadLeadSource = async () => {
      try {
        setLeadSourceHistory(null);
        setLeadSourceError(null);
        const [history, catalog] = await Promise.all([
          apiClient.listCustomerLeadSourceHistory(id),
          apiClient.listLeadSources(),
        ]);
        setLeadSourceHistory(history);
        setLeadSources(catalog);
      } catch (err) {
        setLeadSourceError(getErrorMessage(err, 'Failed to load lead source history'));
      }
    };

    loadLeadSource();
  }, [apiClient, id]);

  // The catalog feeds the add-discount selects; only needed by users who can edit.
  useEffect(() => {
    if (!canEditProperties) return;

    const loadDiscountCatalog = async () => {
      try {
        setDiscountCatalogError(null);
        setDiscountCatalog(await apiClient.listDiscounts());
      } catch (err) {
        setDiscountCatalog([]);
        setDiscountCatalogError(getErrorMessage(err, 'Failed to load discounts'));
      }
    };

    loadDiscountCatalog();
  }, [apiClient, canEditProperties]);

  const attachCustomerDiscount = async (discountId: string) => {
    if (!id) return;
    setCustomerDiscounts(await apiClient.attachCustomerDiscount(id, discountId));
  };

  const detachCustomerDiscount = async (discountId: string) => {
    if (!id) return;
    setCustomerDiscounts(await apiClient.detachCustomerDiscount(id, discountId));
  };

  const loadPropertyDiscounts = async (propertyId: string) => {
    try {
      setPropertyDiscountErrors((prev) => {
        const next = { ...prev };
        delete next[propertyId];
        return next;
      });
      const list = await apiClient.listPropertyDiscounts(propertyId);
      setPropertyDiscounts((prev) => ({ ...prev, [propertyId]: list }));
    } catch (err) {
      setPropertyDiscountErrors((prev) => ({
        ...prev,
        [propertyId]: getErrorMessage(err, 'Failed to load discounts'),
      }));
    }
  };

  const togglePropertyDiscounts = (propertyId: string) => {
    if (expandedDiscountPropertyId === propertyId) {
      setExpandedDiscountPropertyId(null);
      return;
    }
    setExpandedDiscountPropertyId(propertyId);
    if (!propertyDiscounts[propertyId]) {
      loadPropertyDiscounts(propertyId);
    }
  };

  const attachPropertyDiscount = async (propertyId: string, discountId: string) => {
    const list = await apiClient.attachPropertyDiscount(propertyId, discountId);
    setPropertyDiscounts((prev) => ({ ...prev, [propertyId]: list }));
  };

  const detachPropertyDiscount = async (propertyId: string, discountId: string) => {
    const list = await apiClient.detachPropertyDiscount(propertyId, discountId);
    setPropertyDiscounts((prev) => ({ ...prev, [propertyId]: list }));
  };

  /**
   * Refetch the property list after a change. The customer is refetched too
   * so the summary's property count stays in sync; a failure there is ignored
   * because the list itself is already up to date.
   */
  const refreshProperties = useCallback(
    async (customerId: string) => {
      const [propertiesResult, customerResult] = await Promise.allSettled([
        apiClient.listProperties(customerId),
        apiClient.getCustomer(customerId),
      ]);

      if (propertiesResult.status === 'fulfilled') {
        setProperties(propertiesResult.value.data);
      } else {
        setPropertiesError(getErrorMessage(propertiesResult.reason, 'Failed to load properties'));
      }
      if (customerResult.status === 'fulfilled') {
        setCustomer(customerResult.value);
      }
    },
    [apiClient]
  );

  const handleDeleteProperty = async (property: types.Property) => {
    if (!id) return;

    try {
      setDeletingPropertyId(property.id);
      setPropertiesError(null);

      await apiClient.deleteProperty(property.id);
      setConfirmDeletePropertyId(null);
      await refreshProperties(id);
    } catch (err) {
      setConfirmDeletePropertyId(null);
      setPropertiesError(getErrorMessage(err, 'Failed to delete property'));
    } finally {
      setDeletingPropertyId(null);
    }
  };

  const cityStateZip = customer
    ? [[customer.city, customer.state].filter(Boolean).join(', '), customer.zip]
        .filter(Boolean)
        .join(' ')
    : '';

  const hasAddress = Boolean(customer?.address || customer?.city || customer?.state || customer?.zip);
  const initials = customer
    ? customer.name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('')
    : '';

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Page actions */}
      <div className="mb-6 flex flex-wrap justify-between items-center gap-3">
        <button
          onClick={() => navigate('/customers')}
          className="inline-flex items-center gap-1 min-h-[44px] sm:min-h-0 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Customers
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {canStatement && customer && (
            <Link to={`/customers/${customer.id}/statement`} className={`${secondaryButtonClass} inline-flex items-center min-h-[44px] sm:min-h-0`}>
              Statement
            </Link>
          )}
          {canManage && customer && (
            <button
              onClick={() => navigate(`/customers/${customer.id}/edit`)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 sm:py-2 px-4 rounded-lg transition duration-200 shadow-sm"
            >
              Edit Customer
            </button>
          )}
        </div>
      </div>

      {error && <Toast kind="error">{error}</Toast>}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {!isLoading && customer && (
        <div className="space-y-6">
          {/* Identity Banner */}
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-bold">
              {initials || '?'}
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 break-words sm:truncate">{customer.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <CustomerStatusBadge status={customer.status} />
                <Badge tone={customer.taxExempt ? 'gray' : 'blue'}>
                  {BILLING_METHOD_LABELS[customer.billingMethod] ?? customer.billingMethod}
                </Badge>
                {customer.taxExempt && <Badge tone="green">Tax Exempt</Badge>}
                {customer.autoInvoiceExempt && <Badge tone="gray">No auto-invoicing</Badge>}
                {customer.dunningExempt && <Badge tone="gray">No overdue reminders</Badge>}
                <span className="text-sm text-gray-500">
                  Customer since {new Date(customer.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Summary (only when the backend provides it) */}
          {customer.summary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
              <StatCard label="Total Invoiced" value={formatMoney(customer.summary.totalInvoiced)} />
              <StatCard label="Total Paid" value={formatMoney(customer.summary.totalPaid)} />
              <StatCard label="Balance Due" value={formatMoney(customer.summary.balanceDue)} accent={Number(customer.summary.balanceDue) > 0} />
              <StatCard label="Invoices" value={customer.summary.invoiceCount} />
              <StatCard label="Properties" value={customer.summary.propertyCount} />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contact Info */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Contact</h2>
              <dl className="space-y-4">
                <Field icon="mail" label="Email" value={customer.email} />
                <Field icon="phone" label="Phone" value={customer.phone} />
              </dl>
            </section>

            {/* Address */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Billing Address</h2>
              {hasAddress ? (
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div className="min-w-0 break-words text-gray-900 leading-relaxed">
                    {customer.address && <div>{customer.address}</div>}
                    {cityStateZip && <div>{cityStateZip}</div>}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No address on file</p>
              )}
            </section>
          </div>

          {/* Communication history and notifications opt-out (tracker item 35) */}
          {canSendNotifications(user) && (
            <CustomerCommunicationPanel customer={customer} canEdit={canManage} onCustomerChange={(updated) => setCustomer((c) => (c ? { ...c, ...updated } : c))} />
          )}

          {/* Customer portal logins: invite, resend, disable, unlink (tracker item 45) */}
          {canManageCustomerPortal(user) && <CustomerPortalPanel customer={customer} />}

          {/* Where the customer stands in portal rollout campaigns (tracker item 81); read-only */}
          {canManagePortalRollout(user) && <CustomerRolloutPanel customerId={customer.id} />}

          {/* Unapplied payment credit (tracker item 55) */}
          {canManageJobs(user) && <CustomerCreditCard customerId={customer.id} />}

          {/* Cards on file and autopay (tracker item 38) */}
          {canUseOnlinePayments(user) && <CustomerPaymentMethodsCard customerId={customer.id} customerName={customer.name} />}

          {/* Lead Source */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Lead Source</h2>
            <LeadSourcePanel
              currentId={customer.leadSourceId ?? null}
              sources={leadSources}
              history={leadSourceHistory}
              loadError={leadSourceError}
            />
          </section>

          {/* Default dispatch route (tracker item 57; office roles) */}
          <DefaultRoutePanel
            entity="customer"
            entityId={customer.id}
            routeId={customer.routeId ?? null}
            api={apiClient}
            canEdit={canManage}
            onSaved={(routeId) => setCustomer((c) => (c ? { ...c, routeId } : c))}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6"
            headingClassName="text-sm font-semibold text-gray-900 uppercase tracking-wide"
          />

          {/* Customer Discounts */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Discounts{' '}
                {customerDiscounts && customerDiscounts.length > 0 && (
                  <span className="text-gray-400 font-normal">({customerDiscounts.length})</span>
                )}
              </h2>
              <p className="mt-1 text-sm text-gray-500">Applies to all of this customer's properties.</p>
            </div>
            <DiscountAttachmentPanel
              idPrefix="customer"
              attached={customerDiscounts}
              loadError={customerDiscountsError}
              catalog={discountCatalog}
              catalogError={discountCatalogError}
              canManage={canEditProperties}
              emptyMessage="No discounts attached to this customer"
              onAttach={attachCustomerDiscount}
              onDetach={detachCustomerDiscount}
            />
          </section>

          {/* Properties */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Properties {properties.length > 0 && <span className="text-gray-400 font-normal">({properties.length})</span>}
              </h2>
              {canEditProperties && (
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => navigate(`/jobs/new?customerId=${encodeURIComponent(id ?? '')}`)} className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`}>
                    New Job
                  </button>
                  <button onClick={() => navigate(`/customers/${id}/properties/new`)} className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`}>
                    Add Property
                  </button>
                </div>
              )}
            </div>

            {propertiesError && <Toast kind="error">{propertiesError}</Toast>}
            {propertiesError && <p className="text-gray-500 text-sm text-center py-8">Properties could not be loaded.</p>}

            {!propertiesError && properties.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">No properties for this customer</p>
            ) : (
              properties.length > 0 && (
                <ul className="divide-y divide-gray-100">
                  {properties.map((property) => {
                    const isConfirming = confirmDeletePropertyId === property.id;
                    const isDeleting = deletingPropertyId === property.id;
                    const isDiscountsExpanded = expandedDiscountPropertyId === property.id;
                    const attachedDiscounts = propertyDiscounts[property.id];
                    return (
                      <li key={property.id} className="py-3 first:pt-0 last:pb-0">
                        <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 sm:gap-3">
                          <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                          <div className="min-w-0 flex-1">
                            <p className="break-words sm:truncate">
                              <Link to={propertyDetailPath(property)} className="text-blue-700 hover:underline font-medium">{property.address}</Link>
                            </p>
                            <p className="text-sm text-gray-500 break-words">
                              {[property.city, property.state, property.zip].filter(Boolean).join(', ') || '—'}
                              {property.taxRate && (
                                <span className="text-gray-400">
                                  {' · '}
                                  {property.taxRate.name} ({formatRatePercent(property.taxRate.rate)})
                                </span>
                              )}
                              {formatSize(property.sizeValue, property.sizeUnit) && (
                                <span className="text-gray-400">
                                  {' · '}
                                  {formatSize(property.sizeValue, property.sizeUnit)}
                                </span>
                              )}
                            </p>
                            {attachedDiscounts && attachedDiscounts.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {attachedDiscounts.map((discount) => (
                                  <Badge key={discount.id} tone="green">
                                    {discount.name}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          {!canEditProperties && (
                            <div className="w-full pl-8 sm:w-auto sm:pl-0 flex-shrink-0 whitespace-nowrap">
                              <button
                                onClick={() => togglePropertyDiscounts(property.id)}
                                aria-expanded={isDiscountsExpanded}
                                className={`${rowActionClass} py-2.5 sm:py-0`}
                              >
                                {isDiscountsExpanded ? 'Hide Discounts' : 'Discounts'}
                              </button>
                            </div>
                          )}
                          {canEditProperties && (
                            <div className="w-full pl-8 sm:w-auto sm:pl-0 flex-shrink-0 sm:whitespace-nowrap">
                              {isConfirming ? (
                                <span className="inline-flex flex-wrap sm:flex-nowrap items-center gap-x-3 sm:gap-3">
                                  <span className="text-sm text-gray-700">Delete this property?</span>
                                  <button
                                    onClick={() => handleDeleteProperty(property)}
                                    disabled={isDeleting}
                                    className={`${rowDangerActionClass} py-2.5 sm:py-0`}
                                  >
                                    {isDeleting ? 'Deleting...' : 'Confirm'}
                                  </button>
                                  <button
                                    onClick={() => setConfirmDeletePropertyId(null)}
                                    disabled={isDeleting}
                                    className={`${rowActionClass} py-2.5 sm:py-0`}
                                  >
                                    Cancel
                                  </button>
                                </span>
                              ) : (
                                <span className="inline-flex flex-wrap sm:flex-nowrap items-center gap-x-5 sm:gap-4">
                                  <button
                                    onClick={() => togglePropertyDiscounts(property.id)}
                                    aria-expanded={isDiscountsExpanded}
                                    className={`${rowActionClass} py-2.5 sm:py-0`}
                                  >
                                    {isDiscountsExpanded ? 'Hide Discounts' : 'Discounts'}
                                  </button>
                                  <button
                                    onClick={() => navigate(`/customers/${id}/properties/${property.id}/edit`)}
                                    className={`${rowActionClass} py-2.5 sm:py-0`}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => setConfirmDeletePropertyId(property.id)}
                                    disabled={deletingPropertyId !== null}
                                    className={`${rowDangerActionClass} py-2.5 sm:py-0`}
                                  >
                                    Delete
                                  </button>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {isDiscountsExpanded && (
                          <div className="mt-3 sm:ml-8 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4">
                            <p className="mb-3 text-sm text-gray-500">Applies to all services at this property.</p>
                            <DiscountAttachmentPanel
                              idPrefix={`property-${property.id}`}
                              attached={attachedDiscounts ?? null}
                              loadError={propertyDiscountErrors[property.id] ?? null}
                              catalog={discountCatalog}
                              catalogError={discountCatalogError}
                              canManage={canEditProperties}
                              emptyMessage="No discounts attached to this property"
                              onAttach={(discountId) => attachPropertyDiscount(property.id, discountId)}
                              onDetach={(discountId) => detachPropertyDiscount(property.id, discountId)}
                            />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )
            )}
          </section>

          {/* Service bundles: one card per active enrollment (tracker item 85; office roles only) */}
          {canManageBundles(user) && <BundlesSection customerId={customer.id} properties={properties} canManage />}

          {/* Job history */}
          {canSeeJobs && <CustomerJobsSection customerId={customer.id} canOpenEnrollments={canManageBundles(user)} />}

          {/* Documents and photos (tracker item 47; staff only) */}
          {/* Conversation inbox (tracker item 56): shown by the inboxAccess setting */}
          <ConversationsSection customer={{ id: customer.id, name: customer.name }} />

          <AttachmentsSection parentType="CUSTOMER" parentId={customer.id} user={user} />

          {/* Recurring services (staff only) */}
          {canSeeRecurring && (
            <RecurringServicesSection
              customerId={customer.id}
              properties={properties}
              schedules={recurring}
              loadError={recurringError}
              canManage={canEditRecurring}
            />
          )}
        </div>
      )}
    </main>
  );
}

// ============ Components ============

interface FieldProps {
  label: string;
  value?: string | null;
  icon?: 'mail' | 'phone';
}

const FIELD_ICONS: Record<string, React.ReactNode> = {
  mail: (
    <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  phone: (
    <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  ),
};

function Field({ label, value, icon }: FieldProps) {
  return (
    <div className="flex items-center gap-3">
      {icon && <span className="flex-shrink-0">{FIELD_ICONS[icon]}</span>}
      <div className="min-w-0">
        <dt className="text-xs text-gray-500">{label}</dt>
        <dd className="text-gray-900 break-words">{value ? value : '—'}</dd>
      </div>
    </div>
  );
}

interface LeadSourcePanelProps {
  currentId: string | null;
  sources: types.LeadSource[];
  /** null while loading. Newest first. */
  history: types.CustomerLeadSourceHistoryEntry[] | null;
  loadError: string | null;
}

function LeadSourcePanel({ currentId, sources, history, loadError }: LeadSourcePanelProps) {
  const [showHistory, setShowHistory] = useState(false);

  if (loadError) {
    return (
      <><Toast kind="error">{loadError}</Toast><p className="text-gray-500 text-sm">Lead source could not be loaded.</p></>
    );
  }

  if (history === null) {
    return <p className="text-gray-500 text-sm">Loading lead source...</p>;
  }

  const current = currentId ? sources.find((s) => s.id === currentId) : undefined;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs text-gray-500">Current</p>
        <p className="text-gray-900">
          {current ? current.pathLabel : currentId ? 'Unknown source' : 'None'}
          {current && !current.active && (
            <span className="ml-2 align-middle">
              <Badge tone="gray">Inactive</Badge>
            </span>
          )}
        </p>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="py-2.5 sm:py-0 text-xs font-semibold text-blue-600 hover:text-blue-800 uppercase tracking-wide"
        >
          {showHistory ? 'Hide' : 'Show'} Lead Source History
        </button>
        {showHistory && (
          <div className="mt-2">
            {history.length === 0 ? (
              <p className="text-gray-500 text-sm">No lead source changes recorded</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {history.map((entry, index) => (
                  <li key={entry.id} className="flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:gap-3">
                    <div className="min-w-0 flex-1">
                      <p className={entry.leadSourceId ? 'text-gray-900' : 'text-gray-500 italic'}>
                        {entry.leadSourceId
                          ? entry.leadSourcePath ?? 'Deleted source'
                          : 'Cleared (no lead source)'}
                        {index === 0 && (
                          <span className="ml-2 align-middle">
                            <Badge tone="blue">Latest</Badge>
                          </span>
                        )}
                      </p>
                    </div>
                    <p className="flex-shrink-0 text-sm text-gray-500 break-words">
                      {entry.changedByEmail ?? 'Unknown user'} · {new Date(entry.changedAt).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
